import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { UserRole } from '../../../types/api';
import { isSuperAdmin, getRoleDisplayName as getRoleDisplayNameUtil, getRoleColor as getRoleColorUtil } from '../../../utils/roleUtils';
import Icon from '../../atoms/Icon/Icon';
import Button from '../../atoms/Button';
import LanguageSwitcher from '../../atoms/LanguageSwitcher';
import ThemeSwitcher from '../../atoms/ThemeSwitcher/ThemeSwitcher';
import NotificationBell from '../../molecules/NotificationBell/NotificationBell';
import { apiService } from '@/services/api';
import { EventOut } from '@/types/api';
import './RoleBasedLayout.scss';

type TFunction = (key: string) => string;

interface NavLinkItem {
  key: string;
  label: string;
  icon?: string;
  path: string;
}

interface NavGroupHeading {
  key: string;
  label: string;
  group: true;
}

type NavEntry = NavLinkItem | NavGroupHeading;

const isGroupHeading = (entry: NavEntry): entry is NavGroupHeading => 'group' in entry;

/**
 * Navigation for each role, shared by the desktop sidebar and the mobile
 * menu. Organizers/super admins follow the product-first flow: Locales and
 * Eventos, then Inventario (catalog + stock), then Carta (prices per bar).
 */
const getNavigationItems = (userRole: UserRole, t: TFunction): NavEntry[] => {
  const inventory: NavEntry[] = [
    { key: 'inventory-group', label: t('navigation.groups.inventory'), group: true },
    { key: 'products-manage', label: t('products.title'), icon: 'inventario', path: '/products' },
    { key: 'warehouse-stock', label: t('warehouse.navigation.stock'), icon: 'grafico', path: '/warehouse' },
    { key: 'warehouse-history', label: t('warehouse.navigation.history'), icon: 'reloj', path: '/warehouse/history' },
    { key: 'warehouse-alerts', label: t('warehouse.navigation.alerts'), icon: 'advertencia', path: '/warehouse/alerts' },
    { key: 'warehouse-recipes', label: t('warehouse.navigation.recipes'), icon: 'lista', path: '/warehouse/recipes' },
  ];
  const carta: NavEntry[] = [
    { key: 'carta-group', label: t('navigation.groups.carta'), group: true },
    { key: 'drinks-manage', label: t('navigation.drinksCarta'), icon: 'vasos', path: '/drinks/manage' },
    { key: 'foods-manage', label: t('navigation.foodsCarta'), icon: 'cubiertos', path: '/foods/manage' },
  ];
  const head: NavEntry[] = [
    { key: 'dashboard', label: t('navigation.dashboard'), icon: 'panel', path: '/dashboard' },
    { key: 'locals-manage', label: t('locals.title'), icon: 'casa', path: '/locals' },
    { key: 'events-manage', label: t('dashboard.events.manage'), icon: 'eventos', path: '/events/manage' },
  ];
  const management: NavEntry[] = [
    { key: 'management-group', label: t('navigation.groups.management'), group: true },
    { key: 'orders-manage', label: t('dashboard.orders.manage'), icon: 'pedidos', path: '/orders/manage' },
    { key: 'team-manage', label: t('dashboard.team.manage'), icon: 'equipo', path: '/team/manage' },
  ];

  switch (userRole) {
    case 'super_admin':
      return [
        ...head,
        ...inventory,
        ...carta,
        ...management,
        { key: 'global-settings', label: t('navigation.globalSettings'), icon: 'configuraciones', path: '/admin/global-settings' },
        { key: 'country-tax-rates', label: t('navigation.countryTaxRates'), icon: 'signo-dolar', path: '/admin/country-tax-rates' },
        { key: 'users-manage', label: t('navigation.users'), icon: 'equipo', path: '/admin/users' },
        { key: 'profile', label: t('navigation.profile'), icon: 'usuario', path: '/profile' },
      ];
    case 'organizer':
      return [
        ...head,
        ...inventory,
        ...carta,
        ...management,
        { key: 'profile', label: t('navigation.profile'), icon: 'usuario', path: '/profile' },
      ];
    case 'barman':
      return [
        { key: 'bartender', label: t('bartender.navigation.dashboard'), icon: 'cantinero', path: '/barman' },
        { key: 'orders', label: t('bartender.navigation.orders'), icon: 'pedidos', path: '/barman/orders' },
        { key: 'stock', label: t('bartender.navigation.myBarStock'), icon: 'vasos', path: '/barman/stock' },
        { key: 'profile', label: t('navigation.profile'), icon: 'usuario', path: '/profile' },
      ];
    case 'customer':
      return [
        { key: 'events', label: t('navigation.events'), icon: 'eventos', path: '/events' },
        { key: 'orders', label: t('navigation.orders'), icon: 'pedidos', path: '/orders' },
        { key: 'profile', label: t('navigation.profile'), icon: 'usuario', path: '/profile' },
      ];
    case 'warehouse':
      return [
        ...inventory.filter((entry) => !isGroupHeading(entry)),
        { key: 'notifications', label: t('warehouse.navigation.notifications'), icon: 'campana', path: '/notifications' },
        { key: 'profile', label: t('navigation.profile'), icon: 'usuario', path: '/profile' },
      ];
    default:
      return [];
  }
};

interface RoleBasedLayoutProps {
  children: React.ReactNode;
}

const RoleBasedLayout: React.FC<RoleBasedLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed] = useState(true);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Publish the header's real height as --layout-header-height so sticky page
  // elements (e.g. the carta's selection bar) can stick right below it.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const update = () =>
      document.documentElement.style.setProperty('--layout-header-height', `${header.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const [currentEvent, setCurrentEvent] = useState<EventOut | null>(null);
  const [eventLoading, setEventLoading] = useState(true);

  const logoSrc = theme === 'dark' 
    ? '/images/logo/prior-logo-black.png'
    : '/images/logo/prior-logo-white.png';

  // Close menu when clicking outside or on escape key
  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!user?.event_id) {
        setEventLoading(false);
        return;
      }

      try {
        const eventData = await apiService.getEvent(user.event_id);
        setCurrentEvent(eventData);
      } catch (error) {
        console.error('Failed to fetch event information:', error);
      } finally {
        setEventLoading(false);
      }
    };

    fetchEventInfo();

    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [user]);

  if (!user) {
    return <div className="role-based-layout">{children}</div>;
  }

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case 'super_admin':
        return t('roles.superAdmin');
      case 'organizer':
        return t('roles.organizer');
      case 'barman':
        return t('roles.bartender');
      case 'customer':
        return t('roles.customer');
      case 'warehouse':
        return t('roles.warehouse');
      default:
        return role as string;
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'super_admin':
        return <Icon name="ajustes" size={20} alt={t('roles.superAdmin')} />
      case 'organizer':
        return <Icon name="eventos" size={20} alt={t('roles.organizer')} />
      case 'barman':
        return <Icon name="cantinero" size={20} alt={t('roles.bartender')} />
      case 'customer':
        return <Icon name="usuario" size={20} alt={t('roles.customer')} />
      case 'warehouse':
        return <Icon name="vasos" size={20} alt={t('roles.warehouse')} />
      default:
        return <Icon name="usuario" size={20} alt={t('roles.customer')} />
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'super_admin':
        return 'var(--error-color)';
      case 'organizer':
        return 'var(--primary-color)';
      case 'barman':
        return 'var(--success-color)';
      case 'customer':
        return 'var(--info-color)';
      case 'warehouse':
        return 'var(--warning-color)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const navigationItems = getNavigationItems(user.role as UserRole, t);

  return (
    <div className="role-based-layout">
      <header ref={headerRef} className="role-based-layout__header">
        <div className="container">
          {/* Desktop Header */}
          <div className="role-based-layout__header-content role-based-layout__header-content--desktop">
            <div className="role-based-layout__brand">
              <img
                src={logoSrc}
                alt="Prior"
                className="role-based-layout__logo"
              />
            </div>
            <div className="role-based-layout__user-info">
              <div className="role-based-layout__controls">
                <Icon name="idioma" size={20} alt={t('navigation.language')} />
                <LanguageSwitcher />
                <ThemeSwitcher />
                <NotificationBell />
              </div>
              <div className="role-based-layout__user-role">
                <span 
                  className="role-based-layout__role-icon"
                  style={{ color: getRoleColor(user.role as UserRole) }}
                >
                  {getRoleIcon(user.role as UserRole)}
                </span>
                <span className="role-based-layout__role-name">
                  {getRoleDisplayName(user.role as UserRole)}  
                </span>
              {(user.role === 'barman' || user.role === 'warehouse') && !eventLoading && currentEvent && (
                <span className="role-based-layout__working-for-event">
                  {currentEvent?.name}
                </span>
                )}
              </div>
              
              <div className="role-based-layout__user-details">
                <span className="role-based-layout__user-name">
                  {user.first_name || user.username}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={logout}
                  className="role-based-layout__logout"
                >
                  {t('navigation.logout')}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="role-based-layout__header-content role-based-layout__header-content--mobile">
            {/* Logo */}
            <div className="role-based-layout__mobile-logo">
              <img
                src={logoSrc}
                alt="Prior"
                className="role-based-layout__logo-img"
              />
            </div>

            <div className="role-based-layout__mobile-header-actions">
              
              {/* Hamburger Menu Toggle */}
              <button
                className="role-based-layout__mobile-menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                <span className={`role-based-layout__hamburger ${isMenuOpen ? 'role-based-layout__hamburger--open' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="role-based-layout__mobile-overlay" 
          onClick={() => setIsMenuOpen(false)} 
        />
      )}

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="role-based-layout__mobile-menu" ref={mobileMenuRef}>
          <div className="role-based-layout__mobile-menu-content">
            {/* Header del menú con logo y botón cerrar */}
            <div className="role-based-layout__mobile-menu-header">
            <div className="role-based-layout__mobile-menu-logo">
              <img
                src={logoSrc}
                alt="Prior"
                className="role-based-layout__mobile-menu-logo-img"
              />
            </div>
              <button
                className="role-based-layout__mobile-menu-close"
                onClick={() => setIsMenuOpen(false)}
                aria-label={t('common.close')}
              >
                <Icon name="cerrar" size={20} alt={t('common.close')} />
              </button>
            </div>

            {/* Controles en una fila */}
            <div className="role-based-layout__mobile-controls">
              <div className="role-based-layout__mobile-control-item">
                <label>{t('navigation.language')}</label>
                <LanguageSwitcher />
              </div>
              <div className="role-based-layout__mobile-control-item">
                <label>{t('navigation.toggleTheme')}</label>
                <ThemeSwitcher variant="mobile" />
              </div>
            </div>

            {/* Separador */}
            <div className="role-based-layout__mobile-separator"></div>

            {/* User Role Info */}
            <div className="role-based-layout__mobile-user-info">
              <div className="role-based-layout__mobile-user-role">
                <span 
                  className="role-based-layout__mobile-role-icon"
                  style={{ color: getRoleColor(user.role as UserRole) }}
                >
                  {getRoleIcon(user.role as UserRole)}
                </span>
                <div className="role-based-layout__mobile-user-details">
                  <span className="role-based-layout__mobile-role-name">
                    {getRoleDisplayName(user.role as UserRole)}
                  </span>
                  <span className="role-based-layout__mobile-user-name">
                    {user.first_name || user.username}
                  </span>
                </div>
              </div>
            </div>

            {/* Separador */}
            <div className="role-based-layout__mobile-separator"></div>

            {/* Navigation Options */}
            <div className="role-based-layout__mobile-nav-options">
              {navigationItems.map((item) => isGroupHeading(item) ? (
                <div key={item.key} className="role-based-layout__mobile-nav-group">
                  {item.label}
                </div>
              ) : (
                <button
                  key={item.key}
                  className={`role-based-layout__mobile-nav-option ${location.pathname === item.path ? 'role-based-layout__mobile-nav-option--active' : ''}`}
                  onClick={() => {
                    window.location.href = item.path;
                    setIsMenuOpen(false);
                  }}
                >
                  {item.icon && (
                    <span className="role-based-layout__mobile-nav-icon">
                      <Icon name={item.icon} size={20} alt={item.label} />
                    </span>
                  )}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>

            {/* Separador */}
            <div className="role-based-layout__mobile-separator"></div>

            {/* Logout Button */}
            <div className="role-based-layout__mobile-user-actions">
              <button
                className="role-based-layout__mobile-action-btn role-based-layout__mobile-action-btn--logout"
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
              >
                <Icon name="cerrar" size={20} alt={t('navigation.logout')} />
                {t('navigation.logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="role-based-layout__main">
        <div className={`role-based-layout__sidebar ${isSidebarCollapsed ? 'role-based-layout__sidebar--collapsed' : ''}`}>
          <RoleBasedNavigation role={user.role as UserRole} />
        </div>
        
        <div className="role-based-layout__content">
          {children}
        </div>
      </main>
    </div>
  );
};

interface RoleBasedNavigationProps {
  role: UserRole;
}

const RoleBasedNavigation: React.FC<RoleBasedNavigationProps> = ({ role }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const navigationItems = getNavigationItems(role, t);

  return (
    <nav className="role-based-navigation">
      <ul className="role-based-navigation__list">
        {navigationItems.map((item) => {
          if (isGroupHeading(item)) {
            return (
              <li key={item.key} className="role-based-navigation__group" title={item.label}>
                <span className="role-based-navigation__group-label">{item.label}</span>
              </li>
            );
          }
          const isActive = location.pathname === item.path;
          return (
            <li key={item.key} className="role-based-navigation__item">
              <a
                href={item.path}
                className={`role-based-navigation__link ${isActive ? 'role-based-navigation__link--active' : ''} ${!item.icon ? 'role-based-navigation__link--text-only' : ''}`}
              >
                {item.icon && (
                  <span className="role-based-navigation__icon">
                    <Icon name={item.icon} size={20} alt={item.label} />
                  </span>
                )}
                <span className="role-based-navigation__label">{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default RoleBasedLayout; 